import type { KeyboardEvent, PointerEvent as EventoPuntero, ReactNode } from 'react';
import type { Decoracion } from '../../api/ops/sala';
import { GROSOR_PARED, estaCerrada, letraDe, nombreDePieza, puntosAbiertos, puntosDe } from './decoracion';
import './decoracion.css';

/**
 * La decoración de una sala dibujada en SVG con las medidas de la rejilla del plano (8.6 y 8.7, G882): pared por puntos
 * (línea gruesa en tinta; cerrada, con Z), barra de nogal con taburetes del lado de la sala, escenario discontinuo, puerta
 * con su hoja y su apertura hacia dentro (dos hojas si es ancha), planta, texto y otra pieza, con su giro y su nombre. Va
 * siempre debajo de los recursos (se pinta antes que ellos). En la Sala en vivo (`enVivo`) no se pintan las plantas ni la
 * apertura de las puertas ni el nombre de las paredes (menos ruido, 16-oct). En el editor cada pieza se puede tocar.
 */
export interface PiezaEnSala { indice: number; pieza: Decoracion }

interface Props {
    ancho: number;
    alto: number;
    piezas: readonly PiezaEnSala[];
    editando?: boolean;
    enVivo?: boolean;
    elegidas?: ReadonlySet<number>;
    /** La elegida sola (borde continuo); las demás elegidas, discontinuo. */
    elegida?: number | null;
    onPuntero?: (e: EventoPuntero<SVGGElement>, indice: number) => void;
    onTocar?: (indice: number) => void;
    onDobleToque?: (indice: number) => void;
}

const trazo = (pts: readonly (readonly [number, number])[], cerrada: boolean) => `M${pts.map((q) => `${String(q[0])} ${String(q[1])}`).join('L')}${cerrada ? 'Z' : ''}`;

function Forma({ d, ancho, alto, editando, enVivo }: { d: Decoracion; ancho: number; alto: number; editando: boolean; enVivo: boolean }): ReactNode {
    const g = d.geometry;
    const x = g.x ?? 0;
    const y = g.y ?? 0;
    const w = g.width ?? 0;
    const h = g.height ?? 0;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const base = { x, y, width: w, height: h };
    switch (d.kind) {
        case 'wall':
            return <rect {...base} rx={3} style={{ fill: 'var(--rv-ink)' }} />;
        case 'bar': {
            const horizontal = w >= h;
            const largo = horizontal ? w : h;
            const n = Math.max(1, Math.floor((largo - 20) / 56));
            const fuera = horizontal ? (cy < alto / 2 ? 1 : -1) : cx < ancho / 2 ? 1 : -1;
            return (
                <g>
                    {Array.from({ length: n }, (_, i) => {
                        const t = (largo * (i + 1)) / (n + 1);
                        const sx = horizontal ? x + t : fuera > 0 ? x + w + 20 : x - 20;
                        const sy = horizontal ? (fuera > 0 ? y + h + 20 : y - 20) : y + t;
                        return (
                            <g key={i}>
                                <circle cx={sx} cy={sy} r={12} style={{ fill: 'var(--rv-plano-tapiz)', stroke: 'var(--rv-plano-tapiz-respaldo)', strokeWidth: 2 }} />
                                <circle cx={sx} cy={sy} r={5} style={{ fill: 'var(--rv-plano-tapiz-respaldo)' }} />
                            </g>
                        );
                    })}
                    <rect {...base} rx={12} style={{ fill: 'var(--rv-plano-nogal-1)', stroke: 'var(--rv-plano-nogal-2)', strokeWidth: 3 }} />
                    <rect x={x + 6} y={y + 6} width={Math.max(0, w - 12)} height={Math.max(0, h - 12)} rx={8} style={{ fill: 'none', stroke: 'rgba(255,255,255,0.16)', strokeWidth: 2 }} />
                </g>
            );
        }
        case 'stage':
            return <rect {...base} rx={8} style={{ fill: editando ? 'var(--rv-mesa-morado-soft)' : 'var(--rv-paper)', stroke: editando ? 'var(--rv-mesa-morado)' : 'var(--rv-rule-strong)', strokeWidth: 3, strokeDasharray: '10 8' }} />;
        case 'door': {
            const horizontal = w >= h;
            const largo = horizontal ? w : h;
            const dos = largo >= 120;
            const r = Math.min(dos ? largo / 2 : largo, 80);
            const dentro = horizontal ? (cy < alto / 2 ? 1 : -1) : cx < ancho / 2 ? 1 : -1;
            const color = editando ? 'var(--rv-mesa-naranja)' : 'var(--rv-fg-3)';
            const hoja = (px: number, py: number, sg: number, clave: string) => {
                const fx = horizontal ? px : px + dentro * r;
                const fy = horizontal ? py + dentro * r : py;
                const ax = horizontal ? px + sg * r : px;
                const ay = horizontal ? py : py + sg * r;
                const cr = (ax - px) * (fy - py) - (ay - py) * (fx - px);
                return (
                    <g key={clave}>
                        <path d={`M${String(ax)} ${String(ay)}A${String(r)} ${String(r)} 0 0 ${cr > 0 ? '1' : '0'} ${String(fx)} ${String(fy)}`} style={{ fill: 'none', stroke: editando ? color : 'var(--rv-fg-4)', strokeWidth: 2, strokeDasharray: '6 5' }} />
                        <line x1={px} y1={py} x2={fx} y2={fy} style={{ stroke: color, strokeWidth: 3, strokeLinecap: 'round' }} />
                    </g>
                );
            };
            const y0 = horizontal ? (dentro > 0 ? y + h : y) : 0;
            const x0 = horizontal ? 0 : dentro > 0 ? x + w : x;
            return (
                <g>
                    {!enVivo && (
                        <g style={{ pointerEvents: 'none' }}>
                            {horizontal
                                ? dos ? [hoja(x, y0, 1, 'a'), hoja(x + w, y0, -1, 'b')] : hoja(x, y0, 1, 'a')
                                : dos ? [hoja(x0, y, 1, 'a'), hoja(x0, y + h, -1, 'b')] : hoja(x0, y, 1, 'a')}
                        </g>
                    )}
                    <rect {...base} rx={4} style={{ fill: editando ? 'var(--rv-mesa-naranja-soft)' : '#fff', stroke: color, strokeWidth: 3 }} />
                </g>
            );
        }
        case 'plant': {
            const pr = Math.min(w, h) / 2;
            return (
                <g>
                    <circle cx={cx} cy={cy} r={pr * 0.62} style={{ fill: 'var(--rv-plano-roble-3)', stroke: 'var(--rv-plano-canto)', strokeWidth: 2 }} />
                    {[0, 1, 2, 3, 4, 5, 6].map((i) => {
                        const a = i * 0.898;
                        const hx = cx + pr * 0.5 * Math.cos(a);
                        const hy = cy + pr * 0.5 * Math.sin(a);
                        return <ellipse key={i} cx={hx} cy={hy} rx={pr * 0.5} ry={pr * 0.2} transform={`rotate(${String((a * 180) / Math.PI)} ${String(hx)} ${String(hy)})`} style={{ fill: i % 2 === 1 ? 'var(--rv-plano-hoja)' : 'var(--rv-plano-hoja-2)' }} />;
                    })}
                    <circle cx={cx} cy={cy} r={pr * 0.18} style={{ fill: 'var(--rv-plano-hoja-2)' }} />
                </g>
            );
        }
        case 'text':
            return <rect {...base} style={{ fill: 'transparent', stroke: editando ? 'var(--rv-rule)' : 'none', strokeDasharray: '6 6' }} />;
        default:
            return <rect {...base} rx={8} style={{ fill: 'var(--rv-bone)', stroke: 'var(--rv-rule-strong)', strokeWidth: 3 }} />;
    }
}

export function CapaDecoracion({ ancho, alto, piezas, editando = false, enVivo = false, elegidas, elegida = null, onPuntero, onTocar, onDobleToque }: Props) {
    const visibles = piezas.filter((p) => !(enVivo && p.pieza.kind === 'plant'));
    return (
        <svg className="rv-pl-capa" viewBox={`0 0 ${String(ancho)} ${String(alto)}`} preserveAspectRatio="none" aria-hidden={editando ? undefined : true}>
            {visibles.map(({ indice, pieza: d }) => {
                const pts = puntosDe(d);
                const enGrupo = elegidas?.has(indice) === true;
                const sola = elegida === indice;
                const marcada = sola || enGrupo;
                const g = d.geometry;
                const x = g.x ?? 0;
                const y = g.y ?? 0;
                const w = g.width ?? 0;
                const h = g.height ?? 0;
                const giro = pts === null && (g.rotation ?? 0) !== 0 ? `rotate(${String(g.rotation ?? 0)} ${String(x + w / 2)} ${String(y + h / 2)})` : undefined;
                const nombre = (d.label ?? '').trim();
                let dibujo: ReactNode;
                if (pts !== null && pts.length > 1) {
                    const abiertos = puntosAbiertos(pts);
                    const t = trazo(abiertos, estaCerrada(pts));
                    let rotulo: ReactNode = null;
                    if (nombre !== '' && !enVivo) {
                        let mejor = 1;
                        let largo = 0;
                        for (let i = 1; i < abiertos.length; i++) {
                            const a = abiertos[i - 1] ?? [0, 0];
                            const b = abiertos[i] ?? [0, 0];
                            const l = Math.hypot(b[0] - a[0], b[1] - a[1]);
                            if (l > largo) {
                                largo = l;
                                mejor = i;
                            }
                        }
                        const a = abiertos[mejor - 1] ?? [0, 0];
                        const b = abiertos[mejor] ?? [0, 0];
                        const vertical = Math.abs(b[1] - a[1]) > Math.abs(b[0] - a[0]);
                        rotulo = <text x={(a[0] + b[0]) / 2 + (vertical ? 20 : 0)} y={(a[1] + b[1]) / 2 - (vertical ? 0 : 20)} textAnchor={vertical ? 'start' : 'middle'} dominantBaseline="central" className="rv-pl-capa__rotulo" style={{ fontSize: 17 }}>{nombre}</text>;
                    }
                    dibujo = (
                        <>
                            {editando && <path d={t} style={{ fill: 'none', stroke: 'transparent', strokeWidth: GROSOR_PARED + 30, strokeLinecap: 'round', strokeLinejoin: 'round' }} />}
                            {marcada && <path d={t} style={{ fill: 'none', stroke: 'var(--rv-red)', strokeOpacity: 0.28, strokeWidth: GROSOR_PARED + 18, strokeLinecap: 'round', strokeLinejoin: 'round', strokeDasharray: sola ? 'none' : '12 10' }} />}
                            <path d={t} style={{ fill: 'none', stroke: 'var(--rv-ink)', strokeWidth: GROSOR_PARED, strokeLinecap: 'square', strokeLinejoin: 'miter' }} />
                            {rotulo}
                        </>
                    );
                } else if (pts !== null) {
                    dibujo = null;
                } else {
                    dibujo = (
                        <g transform={giro}>
                            <Forma d={d} ancho={ancho} alto={alto} editando={editando} enVivo={enVivo} />
                            {nombre !== '' && (
                                <text
                                    x={x + w / 2}
                                    y={y + h / 2}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    className={`rv-pl-capa__rotulo${d.kind === 'text' ? ' rv-pl-capa__rotulo--texto' : ''}`}
                                    style={{ fontSize: letraDe(d, nombre.length), fill: d.kind === 'bar' ? '#fff' : undefined }}
                                >
                                    {nombre}
                                </text>
                            )}
                            {marcada && <rect x={x - 8} y={y - 8} width={w + 16} height={h + 16} rx={10} style={{ fill: 'none', stroke: 'var(--rv-red)', strokeWidth: 4, strokeDasharray: sola ? 'none' : '10 8' }} />}
                        </g>
                    );
                }
                if (!editando) {
                    return <g key={indice}>{dibujo}</g>;
                }
                const tecla = (e: KeyboardEvent<SVGGElement>) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onTocar?.(indice);
                    }
                };
                return (
                    <g
                        key={indice}
                        role="button"
                        tabIndex={0}
                        aria-label={nombreDePieza(d)}
                        aria-pressed={marcada}
                        className="rv-pl-capa__pieza"
                        onPointerDown={(e) => { onPuntero?.(e, indice); }}
                        onClick={(e) => { e.stopPropagation(); onTocar?.(indice); }}
                        onDoubleClick={() => { onDobleToque?.(indice); }}
                        onKeyDown={tecla}
                    >
                        {dibujo}
                    </g>
                );
            })}
        </svg>
    );
}

/** Muestra de cada tipo en la paleta: el mismo aspecto que tiene en el lienzo (el dibujo de 8.6). */
export function MuestraPieza({ tipo }: { tipo: Decoracion['kind'] }) {
    const contenido = (() => {
        switch (tipo) {
            case 'wall':
                return (
                    <>
                        <path d="M10 32V9H54" style={{ fill: 'none', stroke: 'var(--rv-ink)', strokeWidth: 6, strokeLinejoin: 'miter', strokeLinecap: 'square' }} />
                        {[[10, 32], [10, 9], [54, 9]].map(([x, y]) => <circle key={`${String(x)}-${String(y)}`} cx={x} cy={y} r={3.5} style={{ fill: '#fff', stroke: 'var(--rv-red)', strokeWidth: 2 }} />)}
                    </>
                );
            case 'bar':
                return (
                    <>
                        <rect x={6} y={5} width={52} height={18} rx={5} style={{ fill: 'var(--rv-plano-nogal-1)', stroke: 'var(--rv-plano-nogal-2)', strokeWidth: 2 }} />
                        {[17, 32, 47].map((x) => <circle key={x} cx={x} cy={31} r={4.5} style={{ fill: 'var(--rv-plano-tapiz)', stroke: 'var(--rv-plano-tapiz-respaldo)', strokeWidth: 1.5 }} />)}
                    </>
                );
            case 'stage':
                return <rect x={5} y={6} width={54} height={28} rx={4} style={{ fill: 'var(--rv-mesa-morado-soft)', stroke: 'var(--rv-mesa-morado)', strokeWidth: 2, strokeDasharray: '5 4' }} />;
            case 'door':
                return (
                    <>
                        <path d="M8 10A26 26 0 0 0 34 36" style={{ fill: 'none', stroke: 'var(--rv-mesa-naranja)', strokeWidth: 1.6, strokeDasharray: '3.5 3' }} />
                        <line x1={34} y1={10} x2={34} y2={36} style={{ stroke: 'var(--rv-mesa-naranja)', strokeWidth: 2.4, strokeLinecap: 'round' }} />
                        <rect x={6} y={5} width={52} height={6} rx={2} style={{ fill: 'var(--rv-mesa-naranja-soft)', stroke: 'var(--rv-mesa-naranja)', strokeWidth: 2 }} />
                    </>
                );
            case 'plant':
                return (
                    <>
                        <circle cx={32} cy={20} r={7} style={{ fill: 'var(--rv-plano-roble-3)', stroke: 'var(--rv-plano-canto)', strokeWidth: 1.5 }} />
                        {[0, 1, 2, 3, 4, 5, 6].map((i) => {
                            const a = i * 0.898;
                            const x = 32 + 8 * Math.cos(a);
                            const y = 20 + 8 * Math.sin(a);
                            return <ellipse key={i} cx={x} cy={y} rx={8} ry={3.2} transform={`rotate(${String((a * 180) / Math.PI)} ${String(x)} ${String(y)})`} style={{ fill: i % 2 === 1 ? 'var(--rv-plano-hoja)' : 'var(--rv-plano-hoja-2)' }} />;
                        })}
                    </>
                );
            case 'text':
                return (
                    <>
                        <rect x={6} y={8} width={52} height={24} rx={3} style={{ fill: 'none', stroke: 'var(--rv-rule-strong)', strokeWidth: 1.5, strokeDasharray: '4 4' }} />
                        <text x={32} y={20.5} textAnchor="middle" dominantBaseline="central" style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.12em', fill: 'var(--rv-fg-2)', fontFamily: 'var(--rv-font-sans)' }}>ABC</text>
                    </>
                );
            default:
                return <rect x={14} y={6} width={36} height={28} rx={5} style={{ fill: 'var(--rv-bone)', stroke: 'var(--rv-rule-strong)', strokeWidth: 2.4 }} />;
        }
    })();
    return <svg viewBox="0 0 64 40" width={64} height={40} aria-hidden="true">{contenido}</svg>;
}
